import { Injectable, inject } from '@angular/core';
import { Observable, map, catchError, tap } from 'rxjs';
import {
  AssignmentInputData,
  AssignmentStatus,
  AssignmentQuestion,
} from './assignment.models';
import { AssignmentApiService, AssignmentResponse } from './assignment-api.service';
import { AssignmentStateService } from './assignment-state.service';
import { AssignmentGenerationService } from './assignment-generation.service';
import { AssignmentQuestionsService } from './assignment-questions.service';
import { AssignmentPublishService } from './assignment-publish.service';

/**
 * Facade service that provides a unified API for the assignment wizard.
 * Delegates to focused sub-services for state, generation, questions, and publishing.
 */
@Injectable({
  providedIn: 'root',
})
export class AssignmentService {
  private readonly state = inject(AssignmentStateService);
  private readonly apiService = inject(AssignmentApiService);
  private readonly generation = inject(AssignmentGenerationService);
  private readonly questionsService = inject(AssignmentQuestionsService);
  private readonly publish = inject(AssignmentPublishService);

  // ============================================
  // STATE (delegated to AssignmentStateService)
  // ============================================

  readonly assignmentId$ = this.state.assignmentId$;
  readonly status$ = this.state.status$;
  readonly maxAllowedStep$ = this.state.maxAllowedStep$;
  readonly isLoading$ = this.state.isLoading$;
  readonly currentStep$ = this.state.currentStep$;
  readonly inputData$ = this.state.inputData$;
  readonly uploadedFiles$ = this.state.uploadedFiles$;
  readonly isUploading$ = this.state.isUploading$;
  readonly uploadProgress$ = this.state.uploadProgress$;
  readonly isGenerating$ = this.state.isGenerating$;
  readonly generationProgressStep$ = this.state.generationProgressStep$;
  readonly title$ = this.state.title$;
  readonly questions$ = this.state.questions$;
  readonly shareCode$ = this.state.shareCode$;
  readonly error$ = this.state.error$;

  // ============================================
  // GETTERS (delegated to AssignmentStateService)
  // ============================================

  get currentStep(): number {
    return this.state.currentStep;
  }

  get inputData(): AssignmentInputData {
    return this.state.inputData;
  }

  get assignmentId(): string | null {
    return this.state.assignmentId;
  }

  get status(): AssignmentStatus {
    return this.state.status;
  }

  get maxAllowedStep(): number {
    return this.state.maxAllowedStep;
  }

  get isLoading(): boolean {
    return this.state.isLoading;
  }

  get uploadedFiles(): File[] {
    return this.state.uploadedFiles;
  }

  get title(): string {
    return this.state.title;
  }

  get questions(): AssignmentQuestion[] {
    return this.state.questions;
  }

  get shareCode(): string | null {
    return this.state.shareCode;
  }

  get resolvedSubjectName(): string | null {
    return this.state.resolvedSubjectName;
  }

  set resolvedSubjectName(name: string | null) {
    this.state.resolvedSubjectName = name;
  }

  // ============================================
  // NAVIGATION (delegated to AssignmentStateService)
  // ============================================

  setStep(step: number): void {
    this.state.setStep(step);
  }

  nextStep(): void {
    this.state.nextStep();
  }

  prevStep(): void {
    this.state.prevStep();
  }

  canNavigateToStep(step: number): boolean {
    return this.state.canNavigateToStep(step);
  }

  // ============================================
  // LOAD & INPUT (delegated to AssignmentStateService)
  // ============================================

  loadAssignment(id: string): Observable<AssignmentResponse | null> {
    return this.state.loadAssignment(id);
  }

  updateInputData(data: Partial<AssignmentInputData>): void {
    this.state.updateInputData(data);
  }

  // ============================================
  // FILE UPLOAD (delegated to AssignmentStateService)
  // ============================================

  addFiles(files: File[]): void {
    this.state.addFiles(files);
  }

  removeFile(file: File): void {
    this.state.removeFile(file);
  }

  clearFiles(): void {
    this.state.clearFiles();
  }

  // ============================================
  // EXAMPLE OBJECTIVE
  // ============================================

  /**
   * Generate an example objective based on current input data.
   * Returns the generated objective string and also sets it in the input data.
   */
  generateExampleObjective(): Observable<string> {
    const { grade, tier1SubjectId, tier2SubjectId, state } = this.state.inputData;
    return this.apiService
      .generateExampleObjective({
        grade: grade !== null ? String(grade) : '5',
        ...(tier1SubjectId && { tier1SubjectId }),
        ...(tier2SubjectId && { tier2SubjectId }),
        ...(state && { state }),
      })
      .pipe(
        map((res) => res.objective),
        tap((objective) => this.state.updateInputData({ objective })),
        catchError((err) => {
          this.state.setError(
            'Failed to generate example objective. Please try again.',
          );
          throw err;
        }),
      );
  }

  // ============================================
  // GENERATION (delegated to AssignmentGenerationService)
  // ============================================

  createAssignmentContext(): Observable<void> {
    return this.generation.createAssignmentContext();
  }

  updateObjectiveAndGenerateQuestions(): Observable<AssignmentQuestion[]> {
    return this.generation.updateObjectiveAndGenerateQuestions();
  }

  // ============================================
  // TITLE
  // ============================================

  updateTitle(title: string): void {
    this.state.setTitle(title);
  }

  saveTitle(title: string): Observable<void> {
    this.state.setTitle(title);
    const assignmentId = this.state.assignmentId;
    if (!assignmentId) {
      return new Observable((subscriber) => {
        subscriber.error(new Error('No assignment ID'));
      });
    }
    return this.apiService.updateAssignment(assignmentId, { title }).pipe(
      map(() => undefined),
      catchError((err) => {
        this.state.setError('Failed to save title. Please try again.');
        throw err;
      }),
    );
  }

  // ============================================
  // QUESTIONS (delegated to AssignmentQuestionsService)
  // ============================================

  updateQuestions(questions: AssignmentQuestion[]): void {
    this.questionsService.updateQuestions(questions);
  }

  replaceQuestionLocally(question: AssignmentQuestion): void {
    this.questionsService.replaceQuestionLocally(question);
  }

  saveQuestions(): Observable<void> {
    return this.questionsService.saveQuestions();
  }

  // ============================================
  // SETTINGS & PUBLISHING (delegated to AssignmentPublishService)
  // ============================================

  saveSettings(settings: {
    dueDate: string | null;
    timeLimit: number;
  }): Observable<void> {
    return this.publish.saveSettings(settings);
  }

  publishAssignment(): Observable<void> {
    return this.publish.publishAssignment();
  }

  assignToUser(): Observable<void> {
    return this.publish.assignToUser();
  }

  // ============================================
  // RESET (delegated to AssignmentStateService)
  // ============================================

  reset(): void {
    this.state.reset();
  }
}
